import React from 'react';
import { Box, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import { RegionPricing } from '../../services/api';

const money = (value: number) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 5,
}).format(value);

interface Props {
  pricing?: RegionPricing | null;
  instanceType?: string | null;
}

const changedType = ({ pricing, instanceType }: Props) =>
  !!instanceType && !!pricing && pricing.instance_type !== instanceType;

export const InstancePriceCell: React.FC<Props> = (props) => {
  const { pricing } = props;
  const changed = changedType(props);
  return <Box sx={{ minWidth: 125 }}>
    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
      {!changed && pricing?.instance_hourly != null ? `${money(pricing.instance_hourly)}/hr` : 'Unavailable'}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {changed ? 'Instance changed · sync prices' : pricing?.instance_type || 'Instance not synced'}
    </Typography>
  </Box>;
};

export const TrafficPriceCell: React.FC<Props> = ({ pricing }) => <Box sx={{ minWidth: 125 }}>
  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
    {pricing?.egress_per_gb != null ? `${money(pricing.egress_per_gb)}/GB` : 'Unavailable'}
  </Typography>
  <Typography variant="caption" color="text.secondary">Internet · first paid tier</Typography>
</Box>;

export const RegionPricingDetail: React.FC<Props> = (props) => {
  const { pricing } = props;
  const stale = pricing?.checked_at && Date.now() - Date.parse(pricing.checked_at) > 24 * 60 * 60 * 1000;
  return <Paper variant="outlined" sx={{ p: 2, flex: '1 1 340px', minWidth: 0 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
      <Typography variant="subtitle2">Compute &amp; outgoing traffic</Typography>
      <Chip size="small" variant="outlined" label="USD list prices" />
      {stale && <Chip size="small" variant="outlined" color="warning" label="Refresh recommended" />}
    </Stack>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 1.5 }}>
      <Box><Typography variant="caption" color="text.secondary">Per running instance</Typography><InstancePriceCell {...props} /></Box>
      <Box><Typography variant="caption" color="text.secondary">Outgoing traffic</Typography><TrafficPriceCell {...props} /></Box>
    </Box>
    {!!pricing?.egress_tiers.length && <Box component="dl" sx={{ m: 0, mb: 1.5 }} aria-label="Monthly internet egress tiers">
      {pricing.egress_tiers.map((tier) => <Box key={tier.begin_gb} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography component="dt" variant="caption" color="text.secondary">
          {tier.begin_gb.toLocaleString('en-US')}–{tier.end_gb == null ? '∞' : tier.end_gb.toLocaleString('en-US')} GB/month
        </Typography>
        <Typography component="dd" variant="caption" sx={{ m: 0, fontFamily: 'monospace' }}>{money(tier.price_per_gb)}/GB</Typography>
      </Box>)}
    </Box>}
    <Typography variant="caption" color="text.secondary" display="block">
      Linux On-Demand, shared tenancy. Estimates exclude disks, public IPv4, taxes, discounts and free allowances.
      Traffic tiers apply to aggregated monthly usage; inter-region traffic has separate rates.
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      {pricing?.checked_at ? `Checked ${new Date(pricing.checked_at).toLocaleString()}. ` : ''}
      Refresh using Sync → Capacity &amp; pricing.
    </Typography>
    {(!pricing || pricing.message) && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      {pricing?.message || 'Prices have not been synced for this location.'}
    </Typography>}
    {pricing?.source_url && <Link href={pricing.source_url} target="_blank" rel="noopener noreferrer" variant="caption">AWS pricing source</Link>}
  </Paper>;
};
